/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

import React, { useRef, useState } from 'react';
import { useTheme } from '../../theme/ThemeContext';
import FieldWrapper from '../common/FieldWrapper';
import ThemedInput from '../common/ThemedInput';
import ThemedButton from '../common/ThemedButton';
import FileUploadInput from '../common/FileUploadInput';
import ThemedTextArea from '../common/ThemedTextArea';
import { geocode } from './geocode';

/**
 * Field primitives shared by the event wizard's steps.
 *
 * Nothing here is event-specific — they exist because the wizard needs a
 * select and a coordinate pair styled like the rest of the admin, and neither
 * had an existing themed equivalent.
 */

/**
 * ThemedButton is built for full-width form submits (width: 100%). Buttons that
 * sit inline — wizard navigation, "Add activity", "Remove" — need to size to
 * their label instead, or they stretch across the row and clip.
 */
export const inlineButton = { width: 'auto', padding: '9px 20px', fontSize: 15 };

/** A themed <select>, matching ThemedInput's border and radius. */
export function ThemedSelect({ error, style, children, ...props }) {
  const { theme } = useTheme();
  return (
    <select
      {...props}
      style={{
        border: `1.4px solid ${error ? '#d53b3b' : theme.primary}`,
        borderRadius: theme.buttonRadius,
        background: '#f8fafc',
        color: theme.text,
        fontFamily: 'inherit',
        width: '100%',
        padding: '8px 11px',
        outline: 'none',
        ...(style || {}),
      }}
    >
      {children}
    </select>
  );
}

/** A labelled select over {value,label} options, with a placeholder row. */
export function SelectField({
  label, required, error, value, onChange, options, placeholder = 'Select…', disabled,
}) {
  return (
    <FieldWrapper label={label} required={required} error={error}>
      <ThemedSelect
        error={error}
        value={value ?? ''}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">{placeholder}</option>
        {/* An entry carrying `options` is a group. Flat lists are untouched, so
            every other field on this form behaves exactly as before. */}
        {options.map((option) => (option.options ? (
          <optgroup key={option.group} label={option.group}>
            {option.options.map((child) => (
              <option key={child.value} value={child.value}>{child.label}</option>
            ))}
          </optgroup>
        ) : (
          <option key={option.value} value={option.value}>{option.label}</option>
        )))}
      </ThemedSelect>
    </FieldWrapper>
  );
}

/** A labelled text/number/datetime input. */
export function TextField({
  label, required, error, value, onChange, type = 'text', placeholder, hint, ...rest
}) {
  return (
    <FieldWrapper label={label} required={required} error={error}>
      <ThemedInput
        type={type}
        error={error}
        value={value ?? ''}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        {...rest}
      />
      {hint && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>{hint}</div>}
    </FieldWrapper>
  );
}

/**
 * A labelled multi-line field with a hard character cap and a live counter.
 *
 * The cap is a screen limit, not a column one — these map to unbounded `text`
 * columns whose `validation_rules.max_length` the platform stores but does not
 * enforce. `maxLength` stops typing at the limit; the counter is what makes the
 * limit visible before someone pastes three pages in and wonders why the tail
 * vanished. It turns red once the field is full so a truncated paste is not
 * mistaken for a complete one.
 */
export function LongTextField({
  label, required, error, value, onChange, rows = 5, maxLength, placeholder, hint,
}) {
  const used = (value || '').length;
  const full = maxLength !== undefined && used >= maxLength;
  return (
    <FieldWrapper label={label} required={required} error={error}>
      <ThemedTextArea
        rows={rows}
        error={error}
        maxLength={maxLength}
        value={value ?? ''}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
      <div style={{
        display: 'flex', justifyContent: 'space-between', gap: 12,
        fontSize: 12, color: '#6b7280', marginTop: 4,
      }}>
        <span>{hint}</span>
        {maxLength !== undefined && (
          <span style={{ color: full ? '#b45309' : '#6b7280', whiteSpace: 'nowrap' }}>
            {used} / {maxLength}
          </span>
        )}
      </div>
    </FieldWrapper>
  );
}

/**
 * Location entry for a `geo_point` column.
 *
 * The column stores latitude and longitude, but nobody planning a race knows
 * the coordinates of a start line — they know its name. So the primary input is
 * a place search; the numeric boxes stay underneath for the cases a search
 * cannot resolve, and a map preview confirms the pin landed where it should.
 */
export function PointField({ label, required, error, point, onAxisChange }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const [hovered, setHovered] = useState(-1);
  const abortRef = useRef(null);

  const hasPoint = point?.lat !== '' && point?.lat !== undefined && point?.lat !== null
    && point?.lng !== '' && point?.lng !== undefined && point?.lng !== null;

  const runSearch = async () => {
    if (!query.trim()) return;
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setSearching(true);
    setSearched(false);
    const found = await geocode(query, { signal: controller.signal });
    setResults(found);
    setSearching(false);
    setSearched(true);
  };

  const choose = (result) => {
    onAxisChange('lat', result.lat);
    onAxisChange('lng', result.lng);
    setResults([]);
    setQuery(result.label);
  };

  const paste = (text) => {
    // Accepts "12.9716, 77.5946" — the shape copied out of any maps app.
    const match = String(text).match(/^\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*$/);
    if (!match) return false;
    onAxisChange('lat', match[1]);
    onAxisChange('lng', match[2]);
    return true;
  };

  return (
    <FieldWrapper label={label} required={required} error={error}>
      <div style={{ display: 'flex', gap: 8 }}>
        <ThemedInput
          placeholder="Search a place — e.g. Kanteerava Stadium, Bengaluru"
          value={query}
          error={error}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); runSearch(); }
          }}
        />
        <ThemedButton
          type="button" variant="outlined"
          style={{ width: 'auto', padding: '8px 16px', fontSize: 14, whiteSpace: 'nowrap' }}
          onClick={runSearch}
          disabled={searching || !query.trim()}
        >
          {searching ? 'Searching…' : 'Find'}
        </ThemedButton>
      </div>

      {results.length > 0 && (
        <div style={{
          border: '1px solid #e5e7eb', borderRadius: 8, marginTop: 6,
          background: '#fff', maxHeight: 190, overflowY: 'auto',
        }}>
          {results.map((result, index) => (
            <button
              key={index}
              type="button"
              onClick={() => choose(result)}
              onMouseEnter={() => setHovered(index)}
              onMouseLeave={() => setHovered(-1)}
              onFocus={() => setHovered(index)}
              onBlur={() => setHovered(-1)}
              style={{
                display: 'block', width: '100%', textAlign: 'left', padding: '8px 11px',
                border: 'none', borderBottom: '1px solid #f1f2f4',
                cursor: 'pointer', fontSize: 13,
                // Both colours are set explicitly on every state. A bare
                // `button:hover { color: #fff }` in ApplicationDetailsPage.css is
                // imported globally by CRA, so it reaches every button in the app
                // and would otherwise render this text white on white.
                background: hovered === index ? '#eef2ff' : '#fff',
                color: '#111827',
              }}
            >
              {result.label}
            </button>
          ))}
        </div>
      )}

      {searched && !searching && results.length === 0 && (
        <div style={{ fontSize: 12, color: '#92400e', marginTop: 6 }}>
          Nothing found for that place. Try a fuller address, or enter the coordinates below.
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
        <ThemedInput
          type="number" step="any" placeholder="Latitude" error={error}
          value={point?.lat ?? ''}
          onChange={(e) => onAxisChange('lat', e.target.value)}
          onPaste={(e) => { if (paste(e.clipboardData.getData('text'))) e.preventDefault(); }}
        />
        <ThemedInput
          type="number" step="any" placeholder="Longitude" error={error}
          value={point?.lng ?? ''}
          onChange={(e) => onAxisChange('lng', e.target.value)}
        />
      </div>

      {hasPoint && (
        <iframe
          title={`${label} map`}
          style={{ width: '100%', height: 170, border: '1px solid #e5e7eb', borderRadius: 8, marginTop: 8 }}
          src={`https://www.openstreetmap.org/export/embed.html?bbox=${
            Number(point.lng) - 0.01},${Number(point.lat) - 0.008},${
            Number(point.lng) + 0.01},${Number(point.lat) + 0.008
          }&layer=mapnik&marker=${point.lat},${point.lng}`}
        />
      )}
    </FieldWrapper>
  );
}

/**
 * An uploaded image, with a preview.
 *
 * Uses the platform's own FileUploadInput so the file lands on the file server
 * and the stored value is a URL the browser can actually render — a raw URL box
 * left the admin pasting links by hand and showed nothing back.
 */
export function ImageField({
  label, required, error, value, onChange, name, hint, organizationId, applicationId,
}) {
  return (
    <FieldWrapper label={label} required={required} error={error}>
      <FileUploadInput
        is_public
        value={value || ''}
        onChange={(url) => onChange(url)}
        allowedFileTypes={['image']}
        inputProps={{ name }}
        error={error}
        hint={hint || 'PNG or JPG, up to 1 MB.'}
        organizationId={organizationId}
        applicationId={applicationId}
      />
      {value ? (
        <img
          src={value}
          alt={`${label} preview`}
          style={{
            marginTop: 8, maxHeight: 96, maxWidth: '100%',
            borderRadius: 8, border: '1px solid #e5e7eb', objectFit: 'contain',
            background: '#fff',
          }}
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />
      ) : null}
    </FieldWrapper>
  );
}

/**
 * An uploaded document, for a column that stores its URL.
 *
 * Deliberately not `ImageField`: a proof of identity is a scan or a PDF, and it
 * is not public. `is_public` is left unset, so the file server keeps the file
 * private and the stored value is the OAuth-protected download URL rather than
 * a directly-readable one — which is also why there is no inline preview here.
 * An <img> or an <a> would be unauthenticated and simply fail; the control
 * already shows the stored URL with copy and clear beside it.
 */
export function FileField({
  label, required, error, value, onChange, name, hint,
  organizationId, applicationId,
  allowedFileTypes = ['image', 'document'],
}) {
  return (
    <FieldWrapper label={label} required={required} error={error}>
      <FileUploadInput
        value={value || ''}
        onChange={(url) => onChange(url)}
        allowedFileTypes={allowedFileTypes}
        inputProps={{ name }}
        error={error}
        hint={hint || 'Image or PDF, up to 1 MB. Stored privately.'}
        organizationId={organizationId}
        applicationId={applicationId}
      />
    </FieldWrapper>
  );
}

/** A titled card grouping one section of the form. */
export function Section({ title, subtitle, children, actions }) {
  const { theme } = useTheme();
  return (
    <div
      style={{
        border: '1px solid #e5e7eb',
        borderRadius: 12,
        padding: 20,
        marginBottom: 20,
        background: '#fff',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 16, color: theme.primary }}>{title}</div>
          {subtitle && (
            <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>{subtitle}</div>
          )}
        </div>
        {actions}
      </div>
      <div style={{ marginTop: 16 }}>{children}</div>
    </div>
  );
}

/** Two-column responsive row. */
export function Row({ children }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: '0 16px',
      }}
    >
      {children}
    </div>
  );
}
